// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/LiberacaoDesconto.tsx
//  Workflow de liberação de desconto (paridade desktop frm_libera_desconto).
//  Vendedor solicita desconto acima do limite; gerente aprova ou rejeita.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Percent, Save, Check, X } from 'lucide-react';

import {
  liberacaoDescontoService, STATUS_LIBERACAO,
  type LiberacaoDesconto as Liberacao,
} from '@/features/operacional/services/liberacaoDescontoService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { ProdutoNome } from '@/components/ProdutoNome';
import { UsuarioNome } from '@/components/UsuarioNome';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const brl = (v: number) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (s?: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : s;
};

function statusBadge(status: number) {
  if (status === STATUS_LIBERACAO.APROVADA) return <Badge className="bg-green-500/10 text-green-500">Aprovada</Badge>;
  if (status === STATUS_LIBERACAO.REJEITADA) return <Badge className="bg-red-500/10 text-red-500">Rejeitada</Badge>;
  return <Badge className="bg-yellow-500/10 text-yellow-600">Pendente</Badge>;
}

export default function LiberacaoDesconto() {
  const queryClient = useQueryClient();

  const [novoOpen, setNovoOpen]   = useState(false);
  const [produtoId, setProdutoId] = useState<number | undefined>(undefined);
  const [percentual, setPercentual] = useState(0);
  const [valorOriginal, setValorOriginal] = useState(0);
  const [motivo, setMotivo]       = useState('');

  const [rejeitarId, setRejeitarId] = useState<number | null>(null);
  const [justificativa, setJustificativa] = useState('');

  // Fila de aprovação: abre nas pendentes; situação e período filtram no servidor.
  const [situacao, setSituacao] = useState<string>(String(STATUS_LIBERACAO.PENDENTE));
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });
  const { data: liberacoes = [], isLoading } = useQuery({
    queryKey: ['liberacoes-desconto', situacao, periodo.inicio, periodo.fim],
    queryFn: () => liberacaoDescontoService.getAll(
      situacao === 'todas' ? undefined : Number(situacao), periodo.inicio, periodo.fim),
  });
  const pag = usePaginacaoLocal(liberacoes);

  const valorDesconto = (valorOriginal * percentual) / 100;

  const reset = () => { setProdutoId(undefined); setPercentual(0); setValorOriginal(0); setMotivo(''); };

  const solicitarMutation = useMutation({
    mutationFn: () => {
      if (percentual <= 0 && valorDesconto <= 0) throw new Error('Informe o percentual de desconto');
      return liberacaoDescontoService.solicitar({
        percentualSolicitado: percentual,
        valorOriginal,
        valorDesconto,
        produtoId: produtoId ?? 0,
        motivo: motivo || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liberacoes-desconto'] });
      toast.success('Solicitação enviada para aprovação');
      setNovoOpen(false); reset();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const aprovarMutation = useMutation({
    mutationFn: (id: number) => liberacaoDescontoService.aprovar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liberacoes-desconto'] });
      toast.success('Desconto aprovado');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const rejeitarMutation = useMutation({
    mutationFn: () => {
      if (!justificativa.trim()) throw new Error('Informe a justificativa');
      return liberacaoDescontoService.rejeitar(rejeitarId!, justificativa);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liberacoes-desconto'] });
      toast.success('Solicitação rejeitada');
      setRejeitarId(null); setJustificativa('');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Percent className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Liberação de Desconto</h1>
            <p className="text-sm text-muted-foreground">
              Solicitações de desconto acima do limite — aprovação do gerente.
            </p>
          </div>
        </div>
        <Button className="gap-2" onClick={() => { reset(); setNovoOpen(true); }}>
          <Plus className="h-4 w-4" />Nova Solicitação
        </Button>
      </div>

      <BarraFiltros
        filtrosAtivos={(situacao !== String(STATUS_LIBERACAO.PENDENTE) ? 1 : 0) + (periodo.inicio || periodo.fim ? 1 : 0)}
        onLimpar={() => { setSituacao(String(STATUS_LIBERACAO.PENDENTE)); setPeriodo({ inicio: '', fim: '' }); }}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Situação</Label>
          <Select value={situacao} onValueChange={setSituacao}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Situação"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={String(STATUS_LIBERACAO.PENDENTE)}>Pendentes</SelectItem>
              <SelectItem value={String(STATUS_LIBERACAO.APROVADA)}>Aprovadas</SelectItem>
              <SelectItem value={String(STATUS_LIBERACAO.REJEITADA)}>Rejeitadas</SelectItem>
              <SelectItem value="todas">Todas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo rotulo="Solicitação" inicio={periodo.inicio} fim={periodo.fim} onChange={setPeriodo} />
      </BarraFiltros>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Carregando...</div>
          ) : liberacoes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Percent className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhuma solicitação neste filtro</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Solicitado em</TableHead>
                  <TableHead>Solicitante</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Desconto</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pag.itensPaginados.map((l: Liberacao) => (
                  <TableRow key={l.id}>
                    <TableCell>{fmtData(l.dtSolicitacao)}</TableCell>
                    <TableCell><UsuarioNome id={l.solicitanteId} /></TableCell>
                    <TableCell>{l.produtoId ? <ProdutoNome id={l.produtoId} /> : '—'}</TableCell>
                    <TableCell className="text-right">{brl(l.valorOriginal)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {l.percentualSolicitado}% ({brl(l.valorDesconto)})
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate" title={l.motivo}>{l.motivo ?? '—'}</TableCell>
                    <TableCell>{statusBadge(l.status)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {l.status === STATUS_LIBERACAO.PENDENTE ? (
                        <>
                          <Button size="sm" variant="outline" className="gap-1 text-green-600 border-green-600 hover:bg-green-600/10"
                            onClick={() => aprovarMutation.mutate(l.id)} disabled={aprovarMutation.isPending}>
                            <Check className="h-4 w-4" />Aprovar
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1 text-destructive border-destructive hover:bg-destructive/10"
                            onClick={() => { setRejeitarId(l.id); setJustificativa(''); }}>
                            <X className="h-4 w-4" />Rejeitar
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {l.aprovadorId ? <UsuarioNome id={l.aprovadorId} /> : null}
                          {l.justificativaDecisao ? ` — ${l.justificativaDecisao}` : ''}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {liberacoes.length > 0 && (
        <PaginacaoRodape
          pagina={pag.pagina} totalPaginas={pag.totalPaginas} totalItens={pag.totalItens}
          primeiroItem={pag.primeiroItem} ultimoItem={pag.ultimoItem} tamanhoPagina={pag.tamanhoPagina}
          onPaginaChange={pag.setPagina} onTamanhoPaginaChange={pag.setTamanhoPagina}
        />
      )}

      {/* Nova solicitação */}
      <Dialog open={novoOpen} onOpenChange={(o) => { setNovoOpen(o); if (!o) reset(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Solicitação de Desconto</DialogTitle>
            <DialogDescription>Solicite a liberação de um desconto acima do seu limite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Produto (opcional)</Label>
              <ProdutoSearchCombobox value={produtoId} onChange={setProdutoId} placeholder="Buscar produto..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Valor Original</Label>
                <Input type="number" min={0} step="any" value={valorOriginal}
                  onChange={(e) => setValorOriginal(Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>Desconto (%)</Label>
                <Input type="number" min={0} step="any" value={percentual}
                  onChange={(e) => setPercentual(Number(e.target.value))} />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">Valor do desconto: <strong>{brl(valorDesconto)}</strong></p>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                placeholder="Justificativa da solicitação" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNovoOpen(false); reset(); }}>Cancelar</Button>
            <Button className="gap-2" onClick={() => solicitarMutation.mutate()} disabled={solicitarMutation.isPending}>
              <Save className="h-4 w-4" />Solicitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejeitar */}
      <Dialog open={rejeitarId != null} onOpenChange={(o) => { if (!o) { setRejeitarId(null); setJustificativa(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Solicitação</DialogTitle>
            <DialogDescription>Informe o motivo da rejeição.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Justificativa</Label>
            <Textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejeitarId(null); setJustificativa(''); }}>Cancelar</Button>
            <Button variant="destructive" className="gap-2"
              onClick={() => rejeitarMutation.mutate()} disabled={rejeitarMutation.isPending}>
              <X className="h-4 w-4" />Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
